import type { Node } from '@xyflow/react';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
} from './assessment-attack-graph-layout.constants';
import {
  isAttackPathOverlayRole,
  type AttackPathEventNodeData,
} from './attack-path-event-node.types';

type FlowPoint = {
  x: number;
  y: number;
};

const toSize = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
};

const nodeBounds = (
  node: Node<AttackPathEventNodeData>,
): { x: number; y: number; width: number; height: number } => {
  const isFrame = isAttackPathOverlayRole(node.data.role);
  const styleWidth = node.style?.width;
  const styleHeight = node.style?.height;

  return {
    x: node.position.x,
    y: node.position.y,
    width: toSize(
      node.measured?.width ?? node.width ?? styleWidth,
      isFrame ? 0 : NODE_WIDTH,
    ),
    height: toSize(
      node.measured?.height ?? node.height ?? styleHeight,
      isFrame ? 0 : NODE_HEIGHT,
    ),
  };
};

const containsPoint = ({
  point,
  node,
}: {
  point: FlowPoint;
  node: Node<AttackPathEventNodeData>;
}): boolean => {
  const bounds = nodeBounds(node);

  return (
    bounds.width > 0 &&
    bounds.height > 0 &&
    point.x >= bounds.x &&
    point.x <= bounds.x + bounds.width &&
    point.y >= bounds.y &&
    point.y <= bounds.y + bounds.height
  );
};

export const campaignAtFlowPoint = ({
  point,
  nodes,
}: {
  point: FlowPoint;
  nodes: Node<AttackPathEventNodeData>[];
}): {
  campaignId: string;
  frame: Node<AttackPathEventNodeData>;
} | null => {
  const foreground = nodes.find(
    (node) =>
      node.data.role !== 'CAMPAIGN_FRAME' &&
      node.data.campaignId !== null &&
      containsPoint({ point, node }),
  );

  if (foreground?.data.campaignId) {
    const frame =
      nodes.find(
        (node) =>
          node.data.role === 'CAMPAIGN_FRAME' &&
          node.data.campaignId === foreground.data.campaignId,
      ) ?? foreground;

    return { campaignId: foreground.data.campaignId, frame };
  }

  const frame = [...nodes]
    .reverse()
    .find(
      (node) =>
        node.data.role === 'CAMPAIGN_FRAME' && containsPoint({ point, node }),
    );

  if (!frame?.data.campaignId) {
    return null;
  }

  return { campaignId: frame.data.campaignId, frame };
};

export const overlayAtFlowPoint = ({
  point,
  nodes,
}: {
  point: FlowPoint;
  nodes: Node<AttackPathEventNodeData>[];
}): Node<AttackPathEventNodeData> | null => {
  const foreground = nodes.find(
    (node) =>
      !isAttackPathOverlayRole(node.data.role) &&
      containsPoint({ point, node }),
  );

  if (foreground) {
    return null;
  }

  return (
    [...nodes]
      .reverse()
      .find(
        (node) =>
          isAttackPathOverlayRole(node.data.role) &&
          containsPoint({ point, node }),
      ) ?? null
  );
};
